import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'

import ConnectionIcon from 'app/common/presentation/Connection/ConnectionIcon'
import { useConnections } from 'app/connections/application'

import { Button, Chip, Link } from 'components'

import { mapToConnectionName } from '../connectionUrlMapper'
import styles from './Connection.module.scss'

type ConnectionParams = {
  name: string
}

const Connection = () => {
  const { name } = useParams<ConnectionParams>() as ConnectionParams
  const navigate = useNavigate()
  const { t } = useTranslation('connections')

  const { availableConnections } = useConnections()

  const connection = availableConnections?.find(
    (availableConn) => availableConn.name === mapToConnectionName(name),
  )

  const docuLink = t(`available.${connection?.name}.docu`)

  const openDocumentation = () => window.open(docuLink, '_blank', 'noopener,noreferrer')

  return connection ? (
    <div className={styles.connection}>
      <header className={styles.header}>
        <div className={styles.identity}>
          <div className={styles.icon}>
            <ConnectionIcon connection={connection} />
          </div>

          <div className={styles.summary}>
            <div className={styles.titleRow}>
              <h1>{t(`available.${connection.name}.displayName`)}</h1>
              <div className={styles.tags}>
                {connection.capabilities.map((capability) => (
                  <Chip color="white" key={capability}>
                    {t(`capability.${capability}`)}
                  </Chip>
                ))}
              </div>
            </div>
            <div className={styles.provider}>
              <span>{t('details.provider')}</span>
              <Link
                title={t(`available.${connection.name}.provider.title`)}
                to={t(`available.${connection.name}.provider.link`)}
              />
            </div>
          </div>
        </div>
        <div className={styles.actions}>
          {docuLink && (
            <Button onClick={openDocumentation} variant="secondary">
              {t('details.readDocs')}
            </Button>
          )}
          <Button onClick={() => void navigate(`/connections/${name}/connect`)}>
            {t('details.connect')}
          </Button>
        </div>
      </header>
      <div className={styles.content}>
        <div className={styles.resources}>
          {docuLink && (
            <section>
              <h2>{t('details.resources.title')}</h2>
              <Link
                title={t('details.resources.docu')}
                to={t(`available.${connection.name}.docu`)}
              />
            </section>
          )}
          <section>
            <h2>{t('details.support.title')}</h2>
            <Link title={t('details.support.mail')} to={t('details.support.mail')} type="mail" />
          </section>
        </div>
        <div className={styles.information}>
          <section>
            <h2>{t('details.overview')}</h2>
            <div className={styles.description}>
              <Trans
                components={{ a: <a />, h3: <h3 />, li: <li />, ul: <ul /> }}
                i18nKey={`available.${connection.name}.description`}
                ns="connections"
              />
            </div>
          </section>
          <section>
            <h2>{t('details.setup')}</h2>
            <div className={styles.description}>
              <Trans
                components={{ li: <li />, ul: <ul /> }}
                i18nKey={`available.${connection.name}.requirements`}
                ns="connections"
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  ) : (
    <div>{`Connection ${name} not found`}</div>
  )
}

export default Connection
